/**
 * normalizeSession.js — Single source of truth for Swim Vision session data
 * ==========================================================================
 *
 * The backend serializer returns a mixed-case payload (snake_case from DB
 * spread + camelCase for promoted fields). This utility normalizes the session
 * object into a clean, predictable shape that ALL components must use.
 *
 * RULE: Every component that displays stroke count, stroke rate, DPS, race
 *       time, or per-50m splits MUST read from the normalized session.
 *       Never access raw session.stroke_count / session.strokeCount / etc.
 *
 * @author Arena Stats / Paulo Brant
 * @date   2026-04-09
 */

/**
 * Normalize a raw session payload from the API into a canonical shape.
 *
 * @param {Object} raw — Raw session object from GET /sessions/:id
 * @returns {Object} — Normalized session with unified fields
 */
export function normalizeSession(raw) {
  if (!raw) return null;

  const strokeTiming = raw.strokeTiming || null;
  const summary = strokeTiming?.summary || {};
  const strokes = strokeTiming?.strokes || [];
  const competitionSplits = raw.competitionSplits || [];
  const gapAnalysis = raw.gapAnalysis || strokeTiming?.gapAnalysis || null;
  const styleIntervalLimits = raw.styleIntervalLimits || null;
  // v3.10: Race structure (deterministic geometry + real turn boundaries)
  const raceStructure = raw.raceStructure || strokeTiming?.raceStructure || null;

  // ── Canonical stroke count ──
  // v3.9: strokeTiming is the SINGLE SOURCE OF TRUTH.
  // Priority: summary.totalStrokes (from stroke_events_final)
  // Fallback chain for pre-v3.9 data: stroke_count → strokeCount → strokes.length
  const canonicalStrokeCount =
    summary.totalStrokes || raw.stroke_count || raw.strokeCount || strokes.length || 0;

  // ── Canonical stroke rate ──
  const canonicalStrokeRate = raw.stroke_rate || raw.strokeRate || summary.avgStrokeRate || 0;

  // ── Race distance ──
  const raceDistanceM = raw.raceDistanceM || raw.race_distance_m || 50;
  const poolLengthM = raw.pool_length_m || raw.poolLengthM || 50;

  // ── Race timing ──
  const totalTimeOfficial = raw.totalTimeOfficial || raw.total_time_official || null;
  const raceDurationSeconds = raw.raceDurationSeconds || raw.race_duration_seconds || null;
  const raceTimeDisplay = raw.raceTimeDisplay || null;
  const raceTimeSource = raw.raceTimeSource || (totalTimeOfficial ? 'official' : 'estimated');

  // ── DPS (Distance Per Stroke) ──
  const canonicalDPS = raceDistanceM && canonicalStrokeCount > 0
    ? +(raceDistanceM / canonicalStrokeCount).toFixed(2)
    : (raw.dpsMeters || null);

  // ── Per-50m lap blocks ──
  // Priority:
  //   1. v3.10 raceStructure.blocks (real turn boundaries — NEVER uniform)
  //   2. backend competitionSplits (from CompetitionSummary)
  //   3. v3.9 per-lap counts with uniform timeS (degraded)
  //   4. Legacy equal-time-bin fallback (worst)
  const numLaps = Math.max(1, Math.round(raceDistanceM / poolLengthM));
  let lapBlocks = [];
  let lapBlocksSource = 'none';

  if (raceStructure?.blocks?.length > 0) {
    // ═══ v3.10 PREFERRED: Real splits from detected turn boundaries ═══
    lapBlocks = raceStructure.blocks.map((blk, i) => ({
      lap: blk.index != null ? blk.index + 1 : i + 1,
      label: blk.label || `${i * poolLengthM}–${(i + 1) * poolLengthM}m`,
      distanceM: blk.distanceEndM || (i + 1) * poolLengthM,
      timeS: blk.durationS || 0,
      cumulativeS: blk.endTimeS || 0,
      strokes: blk.strokeCount || 0,
      strokeRate: blk.strokeRate || 0,
      strokesPerMeter: blk.strokesPerMeter || 0,
      avgVelocityMs: blk.avgVelocityMs || 0,
      avgIntervalS: blk.avgIntervalS || 0,
      startTimeS: blk.startTimeS || 0,
      endTimeS: blk.endTimeS || 0,
      quality: blk.quality || 'unknown',
      warnings: blk.warnings || [],
      phase: i === 0 ? 'start' : i === raceStructure.blocks.length - 1 ? 'finish' : 'turn',
    }));
    lapBlocksSource = raceStructure.boundariesSource || 'race_structure';
  } else if (competitionSplits.length > 0) {
    // Use backend-computed per-50m splits (authoritative)
    lapBlocks = competitionSplits.map((sp, i) => ({
      lap: i + 1,
      label: sp.label || `${i * poolLengthM}–${(i + 1) * poolLengthM}m`,
      distanceM: sp.distanceM || (i + 1) * poolLengthM,
      timeS: sp.timeS || 0,
      cumulativeS: sp.cumulativeS || 0,
      strokes: sp.strokes || 0,
      strokeRate: sp.strokeRate || 0,
      strokesPerMeter: sp.strokesPerMeter || 0,
      avgVelocityMs: sp.avgVelocityMs || 0,
      phase: i === 0 ? 'start' : i === numLaps - 1 ? 'finish' : 'turn',
    }));
    lapBlocksSource = 'competition_splits';
  } else if (strokes.length > 0 && numLaps > 1) {
    // v3.9: Use per-lap counts from stroke_events_final when available
    // Each stroke has a .lap field assigned by the backend.
    const perLapFromSummary = summary.perLapCounts;
    const strokesHaveLap = strokes.length > 0 && strokes[0].lap != null;
    const effectiveDuration = totalTimeOfficial || raceDurationSeconds || 0;

    if (strokesHaveLap || perLapFromSummary) {
      // v3.9 path: strokes assigned to laps, BUT timeS is still uniform (degraded)
      lapBlocksSource = 'per_lap_uniform';
      const lapDuration = effectiveDuration > 0 ? effectiveDuration / numLaps : 0;
      for (let lap = 1; lap <= numLaps; lap++) {
        const lapStrokes = strokesHaveLap
          ? strokes.filter(s => s.lap === lap)
          : [];
        const lapCount = perLapFromSummary?.[String(lap)]
          || (strokesHaveLap ? lapStrokes.length : 0);
        lapBlocks.push({
          lap,
          label: `${(lap - 1) * poolLengthM}–${lap * poolLengthM}m`,
          distanceM: lap * poolLengthM,
          timeS: +lapDuration.toFixed(2),
          cumulativeS: +(lap * lapDuration).toFixed(2),
          strokes: lapCount,
          strokeRate: lapCount > 0 && lapDuration > 0
            ? +(lapCount / (lapDuration / 60)).toFixed(1)
            : 0,
          strokesPerMeter: lapCount > 0
            ? +(lapCount / poolLengthM).toFixed(2)
            : 0,
          avgVelocityMs: lapDuration > 0
            ? +(poolLengthM / lapDuration).toFixed(2)
            : 0,
          phase: lap === 1 ? 'start' : lap === numLaps ? 'finish' : 'turn',
        });
      }
    } else if (effectiveDuration > 0) {
      // Legacy fallback: divide strokes into laps by equal time bins (degraded)
      lapBlocksSource = 'uniform_time_bins';
      const lapDuration = effectiveDuration / numLaps;
      for (let lap = 0; lap < numLaps; lap++) {
        const lapStart = lap * lapDuration;
        const lapEnd = (lap + 1) * lapDuration;
        const origin = summary.expectedStartS || 0;
        const lapStrokes = strokes.filter(s => {
          const t = s.videoTimeS - origin;
          return t >= lapStart && t < lapEnd;
        });
        lapBlocks.push({
          lap: lap + 1,
          label: `${lap * poolLengthM}–${(lap + 1) * poolLengthM}m`,
          distanceM: (lap + 1) * poolLengthM,
          timeS: +lapDuration.toFixed(2),
          cumulativeS: +((lap + 1) * lapDuration).toFixed(2),
          strokes: lapStrokes.length,
          strokeRate: lapStrokes.length > 0 && lapDuration > 0
            ? +(lapStrokes.length / (lapDuration / 60)).toFixed(1)
            : 0,
          strokesPerMeter: lapStrokes.length > 0
            ? +(lapStrokes.length / poolLengthM).toFixed(2)
            : 0,
          avgVelocityMs: lapDuration > 0
            ? +(poolLengthM / lapDuration).toFixed(2)
            : 0,
          phase: lap === 0 ? 'start' : lap === numLaps - 1 ? 'finish' : 'turn',
        });
      }
    }
  } else if (numLaps === 1) {
    // 50m race — single block
    lapBlocksSource = 'single_block';
    lapBlocks = [{
      lap: 1,
      label: `0–${poolLengthM}m`,
      distanceM: poolLengthM,
      timeS: totalTimeOfficial || raceDurationSeconds || 0,
      cumulativeS: totalTimeOfficial || raceDurationSeconds || 0,
      strokes: canonicalStrokeCount,
      strokeRate: canonicalStrokeRate,
      strokesPerMeter: canonicalStrokeCount > 0 ? +(canonicalStrokeCount / poolLengthM).toFixed(2) : 0,
      avgVelocityMs: (totalTimeOfficial || raceDurationSeconds) > 0
        ? +(poolLengthM / (totalTimeOfficial || raceDurationSeconds)).toFixed(2) : 0,
      phase: 'start',
    }];
  }

  // Ensure lap stroke sum matches canonical count
  const lapStrokeSum = lapBlocks.reduce((s, b) => s + b.strokes, 0);

  return {
    // Pass-through all raw fields
    ...raw,

    // ── Unified canonical fields (USE THESE, not raw fields) ──
    canonicalStrokeCount,
    canonicalStrokeRate,
    canonicalDPS,
    raceDistanceM,
    poolLengthM,
    totalTimeOfficial,
    raceDurationSeconds,
    raceTimeDisplay,
    raceTimeSource,
    numLaps,
    lapBlocks,
    lapBlocksSource,
    lapStrokeSum,
    pacingType: raw.pacingType || null,

    // ── v3.10: Race structure (deterministic geometry + real boundaries) ──
    raceStructure,
    hasRealSplits: lapBlocksSource === 'race_structure' || lapBlocksSource === 'competition_splits',

    // ── v3.9: Gap analysis + style interval limits ──
    gapAnalysis,
    styleIntervalLimits,
    hasGapIssues: (gapAnalysis?.critical_gaps || 0) > 0,
    recoveredStrokes: summary.recoveredStrokes || 0,

    // ── Convenience accessors ──
    strokeTiming,
    strokes,
    summary,
    isMultiLap: numLaps > 1,
    isLongRace: raceDistanceM >= 400,
    isCompleted: raw.status === 'completed',
    isProcessing: raw.status === 'processing' || raw.status === 'pending',
    startToFirstStrokeS: raw.startToFirstStrokeSeconds || raw.start_to_first_stroke_s || null,
    largestGapS: raw.largestGapSeconds || raw.largest_gap_seconds || null,
    countQuality: raw.countQualityFlag || raw.count_quality_flag || null,
    strokeVersion: raw.strokeCounterVersion || raw.stroke_counter_version || null,
    swimmingStyle: raw.swimming_style || raw.swimmingStyle || '',
  };
}

export default normalizeSession;
